import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { loadButtonConfig } from '../pages/HomeScreenSettingsPage';
import type { HomeButton } from '@caro/shared/types';

export function DynamicButtonGrid() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: buttons, isLoading } = useApi<HomeButton[]>(API_ENDPOINTS.USER_BUTTONS);

  if (isLoading) {
    return (
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: '16px',
        alignContent: 'start',
      }}>
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              minHeight: '120px',
              backgroundColor: '#f0f0f0',
              borderRadius: '16px',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        ))}
      </div>
    );
  }

  // If API returns buttons, use those
  if (buttons && buttons.length > 0) {
    return (
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
        gap: '16px',
        alignContent: 'start',
      }}>
        {buttons.map((button) => (
          <button
            key={button.id}
            onClick={() => {
              if (button.actionTarget) {
                const target = typeof button.actionTarget === 'string'
                  ? button.actionTarget
                  : (button.actionTarget as any)?.path || '';
                // External URLs — open in-app browser
                if (target.startsWith('http://') || target.startsWith('https://')) {
                  navigate(`/browser?url=${encodeURIComponent(target)}`);
                } else if (button.actionType === 'navigation' || (button.actionType as string) === 'navigate') {
                  navigate(target);
                } else {
                  navigate(target || '/');
                }
              }
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              minHeight: '120px',
              backgroundColor: 'var(--caro-card-bg)',
              border: `2px solid ${button.color || '#E2E8F0'}20`,
              borderRadius: '16px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              cursor: 'pointer',
            }}
            aria-label={button.label}
          >
            <span style={{ fontSize: 'var(--font-heading)' }}>{button.icon}</span>
            <span style={{ fontSize: 'var(--font-body)', fontWeight: 600, color: 'var(--caro-text)' }}>
              {button.label}
            </span>
          </button>
        ))}
      </div>
    );
  }

  // No API buttons — use local config (per-user preferences from settings)
  const userId = user?.id || 'default';
  const config = loadButtonConfig(userId);
  const enabledButtons = config.filter(b => b.enabled);

  return (
    <div style={{
      flex: 1,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '16px',
      alignContent: 'start',
    }}>
      {enabledButtons.map((btn) => (
        <PlaceholderButton
          key={btn.id}
          icon={btn.icon}
          label={btn.label}
          color={btn.color}
          onClick={() => navigate(btn.path)}
        />
      ))}
    </div>
  );
}

function PlaceholderButton({ icon, label, color, onClick }: { icon: string; label: string; color: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        minHeight: '120px',
        backgroundColor: 'var(--caro-card-bg)',
        border: `2px solid ${color}20`,
        borderRadius: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      <span style={{ fontSize: 'var(--font-heading)' }}>{icon}</span>
      <span style={{ fontSize: 'var(--font-body)', fontWeight: 600, color: 'var(--caro-text)' }}>{label}</span>
    </button>
  );
}
